import { Injectable } from '@angular/core';
import { ReplaySubject, Subscription } from 'rxjs';

import { IWidget } from '../interfaces/IWidget';
import { IWidgetProperty } from '../interfaces/IWidgetProperty';
import { IWidgetSelection, IWidgetSelectOrigin } from '../interfaces/IWidgetSelection';
import { StageWidget } from '../classes/StageWidget';
import { InteractivityEditorService } from './interactivity-editor.service';
import { TimelineDataActions } from '../redux/timeline-data-actions';
import { ReduxService } from '../../../../services/redux.service';
import { InteractivityWidgetDefinitionService } from './interactivity-widget-definition.service';
import { PublicationService } from 'app/services/publication.service';
import { Publication } from 'app/interfaces/publication';

@Injectable()
export class InteractivityWidgetSelectionService {
	public currentlyInTimeSubject = new ReplaySubject(1);
	private _widgetSelection: IWidgetSelection;
	private _hoveredWidgets: { widgets: string[] } = { widgets: [] };
	private _widgetSelectionSubject: ReplaySubject<IWidgetSelection>;
	private _hoveredWidgetsSubject: ReplaySubject<{ widgets: string[] }>;
	private subscriptions = new Subscription();
	ovpTypeAdServices = false;

	constructor(
		private editorService: InteractivityEditorService,
		private reduxService: ReduxService,
		private widgetDefinitionService: InteractivityWidgetDefinitionService,
		private publicationService: PublicationService
	) {
		this._widgetSelection = { widgets: [], commonProperties: [] };
		this._widgetSelectionSubject = new ReplaySubject<IWidgetSelection>(1); // NB 1!
		this._hoveredWidgetsSubject = new ReplaySubject<{ widgets: string[] }>(1); // NB 1!

		this.subscriptions.add(this.editorService.widgetsSubject.subscribe((widgets: IWidget[]) => {
			if (widgets && widgets.length > 0) {
				// filter the selection to remove widgets that don't exist anymore (after cancel / undo)
				this._widgetSelection.widgets = this._widgetSelection.widgets.filter(sw => sw && widgets.some(ew => ew && ew.id === sw.id));
				this.handleSelectionChanged(); // re-submit properties to handle existing selection
			} else { // no widgets (left)
				this.clearSelectedWidgets();
			}
		}));
		this.subscriptions.add(this.widgetDefinitionService.widgetDefinitionsSubject.subscribe(() => {
			if (this._widgetSelection.widgets && this._widgetSelection.widgets.length > 0) {
				this.handleSelectionChanged(); // re-submit properties to handle existing selection
			}
		}
		));
		this.subscriptions.add(this.publicationService.getData().subscribe((publication: Publication) => {
			this.ovpTypeAdServices = publication?.ovpType === 'advertising services';
		}));
	}

	exit() {
		this.subscriptions.unsubscribe();
	}

	setHoveredWidgets(widgetIds: string[]) {
		this._hoveredWidgets.widgets = widgetIds;
		this._hoveredWidgetsSubject.next(this._hoveredWidgets);
	}

	clearHoveredWidgets() {
		this._hoveredWidgets.widgets = [];
		this._hoveredWidgetsSubject.next(this._hoveredWidgets);
	}

	get hoveredWidgetsSubject(): ReplaySubject<{ widgets: string[] }> {
		return this._hoveredWidgetsSubject;
	}

	get hoveredWidgets(): { widgets: string[] } {
		return this._hoveredWidgets;
	}

	get widgetSelection(): IWidgetSelection {
		return this._widgetSelection;
	}

	get widgetSelectionSubject(): ReplaySubject<IWidgetSelection> {
		return this._widgetSelectionSubject;
	}

	get selectedWidgetIds(): string[] {
		const ids: string[] = []; // new Array<string>();
		for (let i = 0, l: number = this._widgetSelection.widgets.length; i < l; i++) {
			const widget: IWidget = this._widgetSelection.widgets[i];
			ids.push(widget.id);
		}
		return ids;
	}

	public addSelectedWidget(widget: IWidget, selectOrigin: IWidgetSelectOrigin, isNew?: boolean): void {
		if (selectOrigin) {
			this._widgetSelection.origin = selectOrigin;
		} else {
			this._widgetSelection.origin = null;
		}
		if (widget && widget.config.preload && isNew && this.ovpTypeAdServices) {
			widget.config.preload = 'false';
		}
		if (widget && this._widgetSelection.widgets.indexOf(widget) === -1) { // not found
			widget.isSelected = true;
			widget.isNew = isNew;
			this._widgetSelection.widgets.push(widget);
			this.handleSelectionChanged();
		}
	}

	public addSelectedWidgetMulti(widgets: IWidget[], selectOrigin: IWidgetSelectOrigin): void {
		if (selectOrigin) {
			this._widgetSelection.origin = selectOrigin;
		} else {
			this._widgetSelection.origin = null;
		}
		widgets.forEach((widget) => {
			if (widget && this._widgetSelection.widgets.indexOf(widget) === -1) { // not found
				widget.isSelected = true;
				this._widgetSelection.widgets.push(widget);
			}
		});
		this.handleSelectionChanged();

	}

	public removeSelectedWidget(widget: IWidget): void {
		const idx: number = this._widgetSelection.widgets.indexOf(widget);
		if (idx > -1) { // found
			widget.isSelected = false;
			this._widgetSelection.widgets.splice(idx, 1);
			this.handleSelectionChanged();
		}
	}

	public removeSelectedWidgetById(widgetId: string): void {
		this._widgetSelection.widgets.filter(
			(widget: IWidget) => widget.id === widgetId
		).map((widget: IWidget) => {
			this.removeSelectedWidget(widget);
		});
	}

	public selectAllWidgets(): void {
		for (let i = 0, l: number = this.editorService.widgets.length; i < l; i++) {
			const widget: IWidget = this.editorService.widgets[i];
			if (widget && this._widgetSelection.widgets.indexOf(widget) === -1) { // not found
				widget.isSelected = true;
				this._widgetSelection.widgets.push(widget);
			}
		}
		this.handleSelectionChanged();
	}

	public clearSelectedWidgets(): void {
		for (let i = 0, l: number = this._widgetSelection.widgets.length; i < l; i++) {
			const widget: IWidget = this._widgetSelection.widgets[i];
			widget.isSelected = false;
		}
		this._widgetSelection.widgets = [];
		this.handleSelectionChanged();
	}

	public selectInBoundingBox(x: number, y: number, w: number, h: number) {
		// corners of shape 1
		const X: number = x;
		const Y: number = y;
		const A: number = x + w;
		const B: number = y + h;

		for (let i = 0, l: number = this.editorService.widgets.length; i < l; i++) {
			const widget: IWidget = this.editorService.widgets[i];

			if (widget instanceof StageWidget) {
				// corners of shape 2
				const X1: number = parseInt(widget.config.xpos, 10);
				const Y1: number = parseInt(widget.config.ypos, 10);
				const A1: number = parseInt(widget.config.xpos, 10) + parseInt(widget.config.width, 10);
				const B1: number = parseInt(widget.config.ypos, 10) + parseInt(widget.config.height, 10);

				// Simple overlapping rect collision test
				if (A < X1 || A1 < X || B < Y1 || B1 < Y) {
					this.removeSelectedWidget(widget);
				} else {
					this.addSelectedWidget(widget, { area: 'stage' });
				}
			}
		}
	}

	public addSelectedWidgetById(id: string, selectOrigin: IWidgetSelectOrigin, isNew?: boolean): void {
		this.addSelectedWidget(this.editorService.getWidgetById(id), selectOrigin, isNew);
	}
	public deleteSelectedWidgets(): void {
		const ids: string[] = this.selectedWidgetIds;
		if (ids.length === 0) {
			return;
		}

		this.reduxService.dispatch({
			type: TimelineDataActions.REMOVE_WIDGETS,
			ids,
			description: `Deleted ${ids.length} widgets`
		});

		this.clearSelectedWidgets();
	}

	public addSelectedWidgetByIdMulti(ids: string[]): void {
		const widgets: IWidget[] = [];
		ids.forEach((id) => {
			widgets.push(this.editorService.getWidgetById(id));
		});
		this.addSelectedWidgetMulti(widgets, { area: 'tracks' });
	}

	getCommonProperties(providedWidgets?: IWidget[]): IWidgetProperty[] {
		// determine common properties
		const widgetConfigs: any[] = [];
		const commonConfig: any = {};
		const widgets = providedWidgets || this._widgetSelection.widgets;
		for (let i = 0, l: number = widgets.length; i < l; i++) {
			const widget: IWidget = widgets[i];
			if (widget && widget.config) {
				const widgetDefaults = this.widgetDefinitionService.getWidgetDefaultsForType(widget.type);
				let flatDefaults: any = Object.assign({}, this.flattenConfig_recursive(widgetDefaults));
				if (widget.fromAppId) {
					// exposed defaults
					const exposedDefaults = this.editorService.getExposedDefaultsForWidget(widget);

					// exposed config
					const exposedConfig: any = {};
					Object.keys(exposedDefaults).map((key: string) => exposedConfig[key] = (widget.config[key] !== undefined) ? widget.config[key] : '');

					flatDefaults = Object.assign({}, this.flattenConfig_recursive(exposedDefaults));
					widgetConfigs.push(Object.assign(flatDefaults, this.flattenConfig_recursive(exposedConfig)));
				} else {
					widgetConfigs.push(Object.assign(flatDefaults, this.flattenConfig_recursive(widget.config)));
				}
				const widgetConfig: any = widgetConfigs[i];
				for (const key in widgetConfig) {
					if (widgetConfig.hasOwnProperty(key)) {
						if (typeof commonConfig[key] === 'undefined') { // missing
							commonConfig[key] = widgetConfig[key];
						} else if (widgetConfig[key] !== commonConfig[key]) { // not equal
							commonConfig[key] = '__...__'; // special value
						}
					}
				}
			} else {
				widgetConfigs.push(null); // NB keep widgetConfigs consistent with _widgetSelection.widgets (equal length)!
			}
		}
		for (const key in commonConfig) {
			if (commonConfig.hasOwnProperty(key)) {
				for (let i = 0, l: number = widgets.length; i < l; i++) {
					const widget: IWidget = widgets[i];
					if (widget && widget.config) {
						const widgetConfig: any = widgetConfigs[i];
						if (typeof widgetConfig[key] === 'undefined') {
							delete commonConfig[key];
						}
					}
				}
			}
		}
		const commonProperties: IWidgetProperty[] = [];
		for (const key in commonConfig) {
			if (commonConfig.hasOwnProperty(key)) {
				const property: IWidgetProperty = this.widgetDefinitionService.getWidgetPropertyFromConfig(key, commonConfig[key]);
				commonProperties.push(property);
			}
		}
		// console.log('[InteractivityWidgetSelectionService] commonProperties: ', commonProperties);

		return commonProperties;
	}

	private flattenConfig_recursive(config: any, prefix: string = '', result: any = {}): any {
		for (const key in config) {
			if (config.hasOwnProperty(key)) {
				if ((prefix + key).match(/^textFormat/i) && typeof config[key] === 'object' && config[key] !== null) { // NB!
					this.flattenConfig_recursive(config[key], prefix + key + '/', result);
				} else if ((prefix + key).match(/^validation/i) && typeof config[key] === 'object' && config[key] !== null) { // NB!
					this.flattenConfig_recursive(config[key], prefix + key + '/', result);
				} else { // non-textFormat or primitive or null
					result[prefix + key] = config[key];
				}
			}
		}

		return result;
	}

	private handleSelectionChanged() {
		this._widgetSelection.commonProperties = this.getCommonProperties();

		// exception for font
		let font: IWidgetProperty = null;
		let embeddedfont: IWidgetProperty = null;
		for (let i = 0, l = this.widgetSelection.commonProperties.length; i < l; i++) {
			const prop: IWidgetProperty = this.widgetSelection.commonProperties[i];
			if (prop.key === 'textFormat/textFormat/font') {
				font = prop; // ref
			}
			if (prop.key === 'textFormat/textFormat/embeddedfont') {
				embeddedfont = prop; // ref
			}
		}
		if (font) {
			font.value = ((embeddedfont && embeddedfont.value) ? embeddedfont.value : -1) + ';' + font.value;
		}

		this._widgetSelectionSubject.next(this._widgetSelection);
	}
}
